import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/di/precon-supporter';
import { CourseId } from '@/domain/precon-supporter/value-objects';
import type {
  ExaminationResponse,
  GradingRequest,
  GradingResponse,
} from '@/domain/precon-supporter/types/examination';

/**
 * 試験データ取得用のカスタムフック
 */
export function useExamination(
  courseId: 'basic' | 'advanced',
  examinationId: number
) {
  const { examinationService } = useServices();
  const [data, setData] = useState<ExaminationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const courseIdObj = CourseId.fromString(courseId);
        const result = await examinationService.fetchExaminationData({
          courseId: courseIdObj,
          examinationId,
        });

        setData(result);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : '試験データの取得に失敗しました'
        );
      } finally {
        setLoading(false);
      }
    };

    // サービスが利用可能な場合のみ実行
    if (examinationService) {
      fetchData();
    }
  }, [courseId, examinationId, examinationService]);

  return { data, loading, error };
}

/**
 * 試験採点送信用のカスタムフック
 */
export function useExaminationGrading() {
  const { examinationService } = useServices();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitGrading = useCallback(
    async (request: GradingRequest): Promise<GradingResponse | null> => {
      if (!examinationService) {
        setError('サービスが利用できません');
        return null;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await examinationService.submitForGrading(request);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '採点の実行に失敗しました';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [examinationService]
  );

  return { submitGrading, loading, error };
}
